import type { ChatOption } from '../types';

/**
 * Shape returned by the AI engine `GET /starters` endpoint. The static
 * fallback uses the same shape so dynamic and fallback paths are
 * interchangeable downstream.
 */
export interface Starter {
  id: string;
  label: string;
  prompt: string;
}

/**
 * Default conversation starters shown when the dynamic endpoint is
 * unavailable (network error, 401, 422, etc.) or while the request is
 * in flight. Acts as the placeholder data for the TanStack Query in
 * `useDynamicStarters`.
 *
 * MUST be exported as a single frozen reference — `useDynamicStarters`
 * uses identity comparison (`data === STATIC_STARTERS`) to derive the
 * `source: 'dynamic' | 'static'` flag fed into analytics.
 */
export const STATIC_STARTERS: readonly Starter[] = Object.freeze([
  {
    id: 'risk-assessment',
    label: 'Risk assessment basics',
    prompt: 'What are the key steps in conducting a risk assessment?',
  },
  {
    id: 'inherent-vs-residual',
    label: 'Inherent vs residual risk',
    prompt: 'What is the difference between inherent and residual risk?',
  },
  {
    id: 'risk-monitoring',
    label: 'Risk monitoring',
    prompt: 'How can I set up automated risk monitoring in RiskSmart?',
  },
  {
    id: 'risk-mitigation',
    label: 'Risk mitigation strategies',
    prompt: 'What are the four main risk treatment strategies?',
  },
  {
    id: 'risk-frameworks',
    label: 'Risk management frameworks',
    prompt: 'What are some common risk management frameworks and standards?',
  },
]);

export const startersToChatOptions = (
  starters: readonly Starter[],
  sendMessage: (message: string) => Promise<void> | void,
  source: 'dynamic' | 'static'
): ChatOption[] =>
  starters.map((starter) => ({
    id: starter.id,
    label: starter.label,
    action: () => sendMessage(starter.prompt),
    source,
  }));
